import { usePermissions } from "./usePermissions";
import { ROLES } from "../constants";

/**
 * Hook personnalisé pour déterminer la route par défaut selon les permissions de l'utilisateur
 */
export const useDefaultRoute = () => {
  const { user, userRole, hasPermission } = usePermissions();

  const getDefaultRoute = () => {
    if (!user || !userRole) {
      return "/unauthorized"; // Rediriger vers une page non autorisée si pas d'utilisateur ou de rôle
    }

    switch (userRole) {
      case ROLES.DIRECTION:
      case ROLES.COUNTRY_MANAGER:
        // Les dirigeants et country managers vont sur la page d'accueil
        return "/";

      case ROLES.RESP_COMMERCIAL:
        // Les responsables commerciaux vont sur les prospects
        return "/prospects";

      case ROLES.VENDEUR:
        // Les vendeurs vont sur les prospects (ils ont canViewAllProspects)
        return "/prospects";

      case ROLES.GES_STOCK:
        // Les gestionnaires de stock vont sur le stock
        return "/stock";

      case ROLES.RESP_BOUTIQUE:
        // Les responsables de boutique vont sur les détails de leur magasin
        if (user?.magasin) {
          return "/stock/sell-point-details/" + user.magasin;
        }
        return "/sales";

      case ROLES.BOUTIQUIER:
        // Les boutiquiers peuvent uniquement créer des ventes
        return "/sales";

      case ROLES.ADMINISTRATEUR:
        // Les administrateurs gèrent les utilisateurs
        return "/users";

      default:
        return "/unauthorized";
    }
  };

  const getAccessibleRoutes = () => {
    const routes = [];

    if (!user || !userRole) return routes;

    // Points de vente/magasins
    if (
      hasPermission("canViewAllStores") ||
      hasPermission("canViewAssignedStore")
    ) {
      routes.push({
        path: "/sell-points",
        name: "Points de vente",
        priority: 1,
      });
    }

    // Stock
    if (
      hasPermission("canViewAllStock") ||
      hasPermission("canViewAssignedStock")
    ) {
      routes.push({
        path: "/stock",
        name: "Stock",
        priority: userRole === ROLES.GES_STOCK ? 10 : 2,
      });
    }

    // Ventes
    if (
      hasPermission("canViewAllSales") ||
      hasPermission("canViewAssignedSales") ||
      hasPermission("canCreateSale")
    ) {
      routes.push({
        path: "/sales",
        name: "Ventes",
        priority: [ROLES.RESP_BOUTIQUE, ROLES.VENDEUR].includes(userRole)
          ? 10
          : 3,
      });
    }

    // Clients
    if (
      hasPermission("canViewAllClients") ||
      hasPermission("canViewAssignedClients")
    ) {
      routes.push({
        path: "/clients/particuliers",
        name: "Clients",
        priority: 4,
      });
    }

    // Prospects
    if (
      hasPermission("canViewAllProspects") ||
      hasPermission("canViewAssignedProspects")
    ) {
      routes.push({
        path: "/prospects",
        name: "Prospects",
        priority: userRole === ROLES.RESP_COMMERCIAL ? 10 : 5,
      });
    }

    // Commerciaux
    if (hasPermission("canViewAllCommercials")) {
      routes.push({ path: "/commercials", name: "Commerciaux", priority: 6 });
    }

    // Trier par priorité (plus haute priorité en premier)
    return routes.sort((a, b) => b.priority - a.priority);
  };

  const getPreferredRoute = () => {
    // Utiliser la même logique que getDefaultRoute pour la cohérence
    return getDefaultRoute();
  };

  return {
    getDefaultRoute,
    getAccessibleRoutes,
    getPreferredRoute,
  };
};
